import { useCallback, useEffect, useRef, useState, type KeyboardEvent as RKeyboardEvent, type PointerEvent as RPointerEvent } from 'react';

/**
 * Drag-to-resize for a panel edge, remembered per browser.
 *   axis 'x': the handle sits on the panel's LEFT edge (side panel); dragging
 *             left makes the panel wider.
 *   axis 'y': the handle sits on the panel's TOP edge (chart); dragging up
 *             makes the panel taller.
 * Arrow keys nudge by 16 px, double-click resets.
 */
export function useResizable(opts: { key: string; axis: 'x' | 'y'; initial: number; min: number; max: () => number }) {
  const { key, axis, initial, min, max } = opts;
  const clamp = useCallback((v: number) => Math.round(Math.max(min, Math.min(Math.max(min, max()), v))), [min, max]);
  const [size, setSize] = useState(() => {
    const saved = Number(localStorage.getItem(key));
    return Number.isFinite(saved) && saved > 0 ? saved : initial;
  });
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ pos: number; size: number } | undefined>(undefined);

  // Keep inside bounds when the window shrinks.
  useEffect(() => {
    const fit = () => setSize((s) => clamp(s));
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [clamp]);

  const save = (v: number) => localStorage.setItem(key, String(v));

  const onPointerDown = (e: RPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    start.current = { pos: axis === 'x' ? e.clientX : e.clientY, size };
    setDragging(true);
  };
  const onPointerMove = (e: RPointerEvent<HTMLElement>) => {
    if (!start.current) return;
    const d = (axis === 'x' ? e.clientX : e.clientY) - start.current.pos;
    setSize(clamp(start.current.size - d));
  };
  const end = () => {
    if (!start.current) return;
    start.current = undefined;
    setDragging(false);
    setSize((s) => (save(s), s));
  };
  const onKeyDown = (e: RKeyboardEvent<HTMLElement>) => {
    const grow = axis === 'x' ? e.key === 'ArrowLeft' : e.key === 'ArrowUp';
    const shrink = axis === 'x' ? e.key === 'ArrowRight' : e.key === 'ArrowDown';
    if (!grow && !shrink) return;
    e.preventDefault();
    setSize((s) => {
      const v = clamp(s + (grow ? 16 : -16));
      save(v);
      return v;
    });
  };
  const reset = () => {
    const v = clamp(initial);
    setSize(v);
    save(v);
  };

  return {
    size,
    dragging,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: end,
      onPointerCancel: end,
      onDoubleClick: reset,
      onKeyDown,
      role: 'separator' as const,
      tabIndex: 0,
      'aria-orientation': (axis === 'x' ? 'vertical' : 'horizontal') as 'vertical' | 'horizontal',
      'aria-valuenow': size,
      'aria-valuemin': min,
    },
  };
}
